import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import * as yup from "yup";
import { useFormik } from "formik";
import AuthLayout from "layouts/AuthLayout";
import AuthTextField from "components/AuthTextField";
import { signUp } from "services/user";
import { FirebaseError } from "firebase/app";
import { setAuthentication, setUser } from "store/userSlice";

const signupValidationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .min(8, "Password should be at least 8 characters")
    .matches(/[0-9]/, `Your password must have at least 1 digit`)
    .matches(/[a-z]/, `Your password must have at least 1 lowercase character`)
    .matches(/[A-Z]/, `Your password must have at least 1 uppercase character`)
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .required("Please re-type your password")
    .oneOf([yup.ref("password")], "Passwords does not match"),
});

const Signup = () => {
  const [firebaseEmailError, setFirebaseEmailError] = useState("");
  const [firebasePasswordError, setFirebasePasswordError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: signupValidationSchema,
    onSubmit: ({ email, password }) => {
      signUp(email, password)
        .then((user) => {
          dispatch(setAuthentication(true));
          dispatch(setUser(user));
          navigate("/");
        })
        .catch((error: FirebaseError) => {
          setFirebaseEmailError("");
          setFirebasePasswordError("");
          switch (error.code) {
            case "auth/email-already-in-use":
              setFirebaseEmailError("Email already in use");
              break;
            case "auth/invalid-email":
              setFirebaseEmailError("Invalid email address");
              break;
            case "auth/weak-password":
              setFirebasePasswordError(
                "Password should be at least 6 characters"
              );
              break;
            default:
              break;
          }
        });
    },
  });

  return (
    <AuthLayout>
      <Stack component={"form"} onSubmit={formik.handleSubmit} spacing={3}>
        <Box>
          <Typography color={"primary.dark"} fontSize={32} fontWeight={700}>
            Sign Up
          </Typography>
          <Typography color={"text.light"} pb={2}>
            Create a new account
          </Typography>
        </Box>

        <AuthTextField
          fullWidth
          label="Email"
          type="text"
          placeholder="Your email address"
          id="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={
            formik.touched.email &&
            (Boolean(formik.errors.email) || Boolean(firebaseEmailError))
          }
          helperText={
            formik.touched.email && (formik.errors.email || firebaseEmailError)
          }
        />
        <AuthTextField
          fullWidth
          label="Password"
          type="password"
          placeholder="Your password"
          id="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={
            formik.touched.password &&
            (Boolean(formik.errors.password) || Boolean(firebasePasswordError))
          }
          helperText={
            formik.touched.email &&
            (formik.errors.password || firebasePasswordError)
          }
        />
        <AuthTextField
          fullWidth
          label="Confirm Password"
          type="password"
          placeholder="Confirm password"
          id="confirmPassword"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
        />
        <Button
          variant="contained"
          type="submit"
          disableElevation
          sx={{
            borderRadius: 3,
            height: 45,
            "&.MuiButton-root": {
              ":hover": {
                backgroundColor: "primary.light",
              },
            },
          }}
        >
          <Typography fontWeight={700} fontSize={12}>
            Sign Up
          </Typography>
        </Button>
        <Typography textAlign={"center"} color={"text.light"}>
          Already have an account?{" "}
          <Link
            component={RouterLink}
            to="/login"
            color={"primary.blue"}
            fontWeight={700}
            underline="hover"
          >
            Login
          </Link>
        </Typography>
      </Stack>
    </AuthLayout>
  );
};

export default Signup;
